import { typeOf } from '@ember/utils';
import { and, not, eq, or } from 'ember-truth-helpers';
import orElse from 'volta/helpers/or-else';
import BvIcon from 'volta/components/bv-icon';
import BvBadge from 'volta/components/bv-badge';
import BvText from 'volta/components/bv-text';
import BvTooltip from 'volta/components/bv-tooltip';
import BvSpinner from 'volta/components/bv-spinner';

import type { TOC } from '@ember/component/template-only';
import type { IBvMenuItem } from 'volta/components/bv-menu';

interface BvMenuItemContentSignature {
  Args: IBvMenuItem;
  Blocks: {
    default: [];
    prefix: [];
    suffix: [];
  };
}

const BvMenuItemContentComponent: TOC<BvMenuItemContentSignature> = <template>
  {{#if (eq (typeOf @icon) 'string')}}
    <BvIcon
      @glyph={{@icon}}
      @color={{or @iconColor @color 'text-subdued'}}
      class='bv-menu-item__icon'
    />
  {{else if @icon}}
    <span class='u-mr-2 u-d-inline-flex u-align-items-center u-justify-content-center'>
      {{@icon}}
    </span>
  {{/if}}
  {{#if @badge}}
    <BvBadge
      @color={{@badge.color}}
      @splitColor={{@badge.splitColor}}
      @text={{@badge.text}}
      class='bv-menu-item__icon'
    />
  {{/if}}
  {{#if (orElse @prefix (has-block 'prefix'))}}
    <div class='bv-menu-item__prefix'>
      {{#if @prefix}}
        {{@prefix}}
      {{else if (has-block 'prefix')}}
        {{yield to='prefix'}}
      {{/if}}
    </div>
  {{/if}}
  <div class='bv-menu-item__content' data-test-menu-content='{{@content}}'>
    {{#if (orElse @content (has-block))}}
      {{#if @title}}
        <BvText @variant='heading-xs' @text={{@title}} class='u-text-upper' />
      {{/if}}
      {{#if @content}}
        {{@content}}
      {{else if (has-block)}}
        {{yield}}
      {{/if}}
    {{else}}
      {{@tooltip}}
    {{/if}}
  </div>
  {{#if (orElse @suffix (has-block 'suffix'))}}
    <div class='bv-menu-item__suffix'>
      {{#if @suffix}}
        {{@suffix}}
      {{else if (has-block 'suffix')}}
        {{yield to='suffix'}}
      {{/if}}
    </div>
  {{/if}}
  {{#if @shortcut}}
    <span class='bv-menu-item__shortcut'>
      {{@shortcut}}
    </span>
  {{/if}}
  {{#if (and @href (not @model))}}
    <BvIcon class='bv-menu-item__arrow u-rotate-45' @glyph='arrow-up' @size='sm' />
  {{else if @hasArrow}}
    <BvIcon class='bv-menu-item__arrow' @glyph='chevron-forward' />
  {{else if @selected}}
    <BvIcon class='bv-menu-item__arrow' @glyph='checkmark' />
  {{/if}}
  {{#if @tooltip}}
    <BvTooltip @text={{@tooltip}} />
  {{/if}}
  {{#if @isLoading}}
    <BvSpinner
      class='u-position-absolute u-right-2 u-shadow-card u-border-radius u-bg-surface u-animation-appear-right u-p-1'
      @size='sm'
    />
  {{/if}}
</template>;

export default BvMenuItemContentComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvMenu::Item::Content': typeof BvMenuItemContentComponent;
    'bv-menu/item/content': typeof BvMenuItemContentComponent;
  }
}
